import { useEffect, useRef, useState, type Ref } from 'react';
import { useTranslation } from 'react-i18next';
import { Minus, Plus, X, RotateCcw, Copy } from 'lucide-react';
import { Pill } from '@/ui/Pill';
import { HomeLayout } from './HomeLayout';

/** the first N inputs of a board cannot be removed */
const FIXED = 2;
const MAX = 999;
/** the client's preset boards; names live in the locale files */
export const TEMPLATES = ['t1', 't2', 't3'] as const;
type Template = (typeof TEMPLATES)[number];

export interface Item {
  id: number;
  name: string;
  value: number;
  /** user-picked colour; otherwise the slot colour */
  color?: string;
}
/** what the numbers mean: combo counts, or percents typed directly */
type Unit = 'combos' | 'percent';
interface Board {
  id: number;
  unit: Unit;
  /** total combos typed by the user in % mode; in combos mode the sum of the inputs is used instead */
  total: number;
  items: Item[];
}

let nextId = 1;
const item = (name = ''): Item => ({ id: nextId++, name, value: 0 });
const clamp = (n: number): number => Math.min(MAX, Math.max(0, Math.round(n || 0)));
/** slot colours run hot to cold in input order: red, orange, yellow, green, teal, blue, violet; the pie reads as a gradient */
const HUE_START = 25;
const HUE_STEP = 34;
/** the sweep never wraps: with many inputs the step shrinks so the last hue stays short of red again */
const slotColor = (i: number, n: number): string =>
  `oklch(72% 0.19 ${(HUE_START + i * Math.min(HUE_STEP, 300 / Math.max(1, n - 1))) % 360})`;
const colorOf = (x: Item, i: number, n: number): string => x.color ?? slotColor(i, n);
const total = (items: Item[]): number => items.reduce((s, x) => s + x.value, 0);
/** share of the whole: of the sum for combos; of 100 (or of the sum when it passes 100) for percents */
const shareOf = (value: number, items: Item[], unit: Unit): number => {
  const sum = total(items);
  const base = unit === 'percent' ? Math.max(100, sum) : sum;
  return base ? value / base : 0;
};

/** Morphology page: boards of named counts, each drawn as a pie and a stack of chips; a board can be duplicated with its data. */
export function MorphologyScreen() {
  const { t } = useTranslation();
  const [template, setTemplate] = useState<Template>('t1');
  const fromTemplate = (tpl: Template, unit: Unit = 'combos'): Board => ({
    id: nextId++,
    unit,
    total: 0,
    items: t(`morphology.tpl.${tpl}.items`)
      .split('|')
      .map((n) => item(n.trim())),
  });
  const [boards, setBoards] = useState<Board[]>(() => [fromTemplate('t1')]);

  const pick = (tpl: Template): void => {
    setTemplate(tpl);
    setBoards([fromTemplate(tpl)]);
  };
  const patch = (id: number, fn: (b: Board) => Board): void =>
    setBoards((bs) => bs.map((b) => (b.id === id ? fn(b) : b)));
  const duplicate = (): void =>
    setBoards((bs) => {
      const last = bs[bs.length - 1]!;
      return [
        ...bs,
        { ...last, id: nextId++, items: last.items.map((x) => ({ ...x, id: nextId++ })) },
      ];
    });

  return (
    <HomeLayout section={t('morphology.title')} backTo="/">
      <div className="mb-6 flex flex-wrap justify-center gap-3">
        {TEMPLATES.map((tpl) => (
          <Pill key={tpl} active={template === tpl} onClick={() => pick(tpl)}>
            {t(`morphology.tpl.${tpl}.name`)}
          </Pill>
        ))}
      </div>

      <div className="flex flex-col gap-6">
        {boards.map((b, i) => (
          <BoardView
            key={b.id}
            board={b}
            onChange={(fn) => patch(b.id, fn)}
            onReset={() => patch(b.id, (old) => fromTemplate(template, old.unit))}
            onRemove={i > 0 ? () => setBoards((bs) => bs.filter((x) => x.id !== b.id)) : undefined}
          />
        ))}
        <div className="flex justify-center">
          <Pill icon={<Copy />} onClick={duplicate}>
            {t('morphology.duplicate')}
          </Pill>
        </div>
      </div>
    </HomeLayout>
  );
}

const stepBtn =
  'grid size-11 shrink-0 place-items-center rounded-xl border border-line-strong bg-surface text-fg-muted transition-colors hover:border-accent hover:text-accent disabled:cursor-default disabled:opacity-40 disabled:hover:border-line-strong disabled:hover:text-fg-muted';
const box = 'enter relative rounded-[20px] border-2 border-accent bg-surface p-6';

/** One board: inputs | pie | chip stack; the chip column is narrow. */
function BoardView({
  board,
  onChange,
  onReset,
  onRemove,
}: {
  board: Board;
  onChange: (fn: (b: Board) => Board) => void;
  onReset: () => void;
  onRemove?: (() => void) | undefined;
}) {
  const { t } = useTranslation();
  const { items, unit } = board;
  const sum = total(items);
  /** combos behind the board: the typed total in % mode, the sum of the inputs otherwise */
  const combos = unit === 'percent' ? board.total : sum;
  const maxValue = unit === 'percent' ? 100 : MAX;
  const setItem = (id: number, fn: (x: Item) => Item): void =>
    onChange((b) => ({ ...b, items: b.items.map((x) => (x.id === id ? fn(x) : x)) }));
  const setValue = (id: number, v: number): void =>
    setItem(id, (x) => ({ ...x, value: Math.min(maxValue, clamp(v)) }));
  // the chip stack is drawn as tall as the pie is rendered, whatever the column width
  const pieRef = useRef<SVGSVGElement>(null);
  const [pieHeight, setPieHeight] = useState(0);
  useEffect(() => {
    const el = pieRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setPieHeight(el.getBoundingClientRect().height));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_260px] md:max-lg:grid-cols-[minmax(0,1fr)_260px]">
      <section
        className={`${box} flex flex-col gap-3`}
        style={{ boxShadow: 'var(--glow)' }}
        aria-label={t('morphology.values')}
      >
        {/* what the numbers mean: combo counts, or percents typed directly; the sum of combos sits beside it */}
        <div className="flex items-center gap-3">
          <span
            className="inline-flex gap-[3px] rounded-full border border-line-strong p-[3px]"
            role="group"
            aria-label={t('morphology.unit.label')}
          >
            {(['combos', 'percent'] as const).map((u) => (
              <button
                key={u}
                type="button"
                aria-pressed={unit === u}
                onClick={() =>
                  u !== unit &&
                  onChange((b) => ({
                    ...b,
                    unit: u,
                    total: 0,
                    items: b.items.map((x) => ({ ...x, value: 0 })),
                  }))
                }
                className="h-[30px] rounded-full px-3.5 text-[11px] font-bold tracking-[0.12em] text-fg-muted uppercase aria-pressed:bg-accent aria-pressed:text-bg"
              >
                {t(`morphology.unit.${u}`)}
              </button>
            ))}
          </span>
          <label className="ml-auto mr-8 flex items-center gap-2 font-mono text-[13px] font-bold text-fg-muted">
            {t('morphology.sum')}
            <input
              type="number"
              inputMode="numeric"
              min={0}
              value={combos}
              readOnly={unit === 'combos'}
              aria-label={t('morphology.sum')}
              onChange={(e) => onChange((b) => ({ ...b, total: clamp(e.target.valueAsNumber) }))}
              className="h-9 w-[72px] rounded-xl border border-line-strong bg-bg text-center font-mono text-[15px] font-bold text-fg outline-none read-only:border-line read-only:text-fg-muted focus:border-accent [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            />
          </label>
        </div>
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            aria-label={t('morphology.removeBoard')}
            className="absolute top-3 right-3 grid size-7 place-items-center rounded-lg text-fg-dim transition-colors hover:bg-surface-2 hover:text-err"
          >
            <X className="size-4" strokeWidth={2.4} />
          </button>
        )}
        {items.map((x, i) => (
          <div key={x.id} className="flex items-center gap-2.5">
            {/* the dot is a colour picker: click it to give this input its own colour */}
            <label
              className="relative size-3 shrink-0 cursor-pointer rounded-full ring-offset-2 ring-offset-surface hover:ring-2 hover:ring-fg-muted"
              style={{ background: colorOf(x, i, items.length) }}
              title={t('morphology.color')}
            >
              <input
                type="color"
                value={toHex(colorOf(x, i, items.length))}
                aria-label={`${t('morphology.color')} ${i + 1}`}
                onChange={(e) => setItem(x.id, (y) => ({ ...y, color: e.target.value }))}
                className="absolute inset-0 size-full cursor-pointer opacity-0"
              />
            </label>
            <input
              type="text"
              value={x.name}
              placeholder={t('morphology.name')}
              aria-label={`${t('morphology.name')} ${i + 1}`}
              onChange={(e) => setItem(x.id, (y) => ({ ...y, name: e.target.value }))}
              className="h-11 w-0 min-w-[72px] flex-1 rounded-xl border border-line-strong bg-bg px-3 font-mono text-[13px] font-bold text-fg outline-none placeholder:font-normal placeholder:text-fg-dim focus:border-accent"
            />
            <button
              type="button"
              onClick={() => setValue(x.id, x.value - 1)}
              disabled={x.value <= 0}
              aria-label="−1"
              className={stepBtn}
            >
              <Minus className="size-5" strokeWidth={2.4} />
            </button>
            <input
              type="number"
              inputMode="numeric"
              min={0}
              max={maxValue}
              value={x.value}
              aria-label={`${t('morphology.value')} ${i + 1}`}
              onChange={(e) => setValue(x.id, e.target.valueAsNumber)}
              className="h-11 w-[64px] rounded-xl border border-line-strong bg-bg text-center font-mono text-[18px] font-bold text-fg outline-none focus:border-accent [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            />
            <button
              type="button"
              onClick={() => setValue(x.id, x.value + 1)}
              disabled={x.value >= maxValue}
              aria-label="+1"
              className={stepBtn}
            >
              <Plus className="size-5" strokeWidth={2.4} />
            </button>
            <span className="w-[4ch] text-right font-mono text-[13px] font-bold text-fg-muted">
              {unit === 'percent'
                ? Math.round((combos * x.value) / 100)
                : sum
                  ? `${Math.round(shareOf(x.value, items, unit) * 100)}%`
                  : ''}
            </span>
            {/* the slot is always there so every row lines up; the first rows just have no cross */}
            {i >= FIXED ? (
              <button
                type="button"
                onClick={() =>
                  onChange((b) => ({ ...b, items: b.items.filter((y) => y.id !== x.id) }))
                }
                aria-label={t('morphology.remove')}
                className="grid size-7 shrink-0 place-items-center rounded-lg text-fg-dim transition-colors hover:bg-surface-2 hover:text-err"
              >
                <X className="size-4" strokeWidth={2.4} />
              </button>
            ) : (
              <span className="size-7 shrink-0" aria-hidden />
            )}
          </div>
        ))}
        <div className="mt-3 flex gap-3 border-t border-line pt-4">
          <button
            type="button"
            onClick={() => onChange((b) => ({ ...b, items: [...b.items, item()] }))}
            aria-label={t('morphology.add')}
            className={stepBtn}
          >
            <Plus className="size-5" strokeWidth={2.4} />
          </button>
          <button
            type="button"
            onClick={onReset}
            aria-label={t('morphology.reset')}
            className={stepBtn}
          >
            <RotateCcw className="size-5" strokeWidth={2.4} />
          </button>
        </div>
      </section>

      <section
        className={`${box} grid place-items-center`}
        style={{ boxShadow: 'var(--glow)', animationDelay: '60ms' }}
        aria-label={t('morphology.diagram')}
      >
        <ChipPie
          items={items}
          unit={unit}
          svgRef={pieRef}
          className="size-[min(100%,440px)] max-w-full overflow-visible"
        />
      </section>

      <section
        className={`${box} grid place-items-center`}
        style={{ boxShadow: 'var(--glow)', animationDelay: '120ms' }}
        aria-label={t('morphology.bar')}
      >
        <ChipStack items={items} unit={unit} height={pieHeight} />
      </section>
    </div>
  );
}

/** the colour input needs a hex value; oklch slot colours are resolved through a canvas */
function toHex(color: string): string {
  if (color.startsWith('#')) return color;
  // resolve var(--accent) and the relative colour through the document, then read the pixel through a canvas
  const probe = document.createElement('span');
  probe.style.color = color;
  document.body.appendChild(probe);
  const resolved = getComputedStyle(probe).color;
  probe.remove();
  const ctx = document.createElement('canvas').getContext('2d');
  if (!ctx) return '#888888';
  ctx.fillStyle = resolved;
  ctx.fillRect(0, 0, 1, 1);
  const [r, g, b] = ctx.getImageData(0, 0, 1, 1).data;
  return '#' + [r, g, b].map((n) => n!.toString(16).padStart(2, '0')).join('');
}

const C = 100;
const R = 60;
const pt = (angle: number, r: number): [number, number] => [
  C + r * Math.cos(angle),
  C + r * Math.sin(angle),
];

interface Slice {
  i: number;
  name: string;
  /** the name wrapped into short lines for the pie label */
  lines: string[];
  share: number;
  start: number;
  end: number;
  color: string;
}
function slices(items: Item[], unit: Unit): Slice[] {
  let angle = -Math.PI / 2;
  return items.flatMap((x, i) => {
    const share = shareOf(x.value, items, unit);
    if (!share) return [];
    const s: Slice = {
      i,
      name: x.name,
      lines: wrap(x.name),
      share,
      start: angle,
      end: angle + share * 2 * Math.PI,
      color: colorOf(x, i, items.length),
    };
    angle = s.end;
    return [s];
  });
}

/** Break a name into lines of at most `max` characters: on spaces first, inside a long word if it must. */
function wrap(name: string, max = 9): string[] {
  const out: string[] = [];
  for (const word of name.trim().split(/\s+/).filter(Boolean)) {
    const parts = word.match(new RegExp(`.{1,${max}}`, 'g')) ?? [];
    for (const part of parts) {
      const last = out[out.length - 1];
      if (last !== undefined && last.length + 1 + part.length <= max)
        out[out.length - 1] = `${last} ${part}`;
      else out.push(part);
    }
  }
  return out;
}

/**
 * Labels outside the pie, one per slice, never overlapping: each side (left / right) is sorted by height and
 * pushed apart to a minimum gap, and a leader line joins the label to its slice.
 */
function placeLabels(
  ss: Slice[],
): { s: Slice; x: number; y: number; anchor: 'start' | 'end'; lx: number; ly: number }[] {
  const out: { s: Slice; x: number; y: number; anchor: 'start' | 'end'; lx: number; ly: number }[] =
    [];
  for (const right of [true, false]) {
    const side = ss
      .map((s) => ({ s, mid: (s.start + s.end) / 2 }))
      .filter(({ mid }) => Math.cos(mid) >= 0 === right)
      .map(({ s, mid }) => ({ s, mid, y: C + (R + 12) * Math.sin(mid) }))
      .sort((a, b) => a.y - b.y);
    // room a label needs above its own centre: its name lines plus the percent line, with a small margin
    const gap = (s: Slice): number => 12 + 6.5 * s.lines.length;
    // push down from the top, pull up from the bottom, then push down once more so nothing sits above the top edge
    const down = (): void => {
      for (let k = 0; k < side.length; k++)
        side[k]!.y = Math.max(side[k]!.y, k ? side[k - 1]!.y + gap(side[k]!.s) : 8);
    };
    down();
    for (let k = side.length - 1; k >= 0; k--) {
      const next = side[k + 1];
      side[k]!.y = Math.min(side[k]!.y, next ? next.y - gap(next.s) : 200 - 8);
    }
    down();
    for (const { s, mid, y } of side) {
      const [lx, ly] = pt(mid, R + 8);
      out.push({
        s,
        x: right ? C + R + 18 : C - R - 18,
        y,
        anchor: right ? 'start' : 'end',
        lx,
        ly,
      });
    }
  }
  return out;
}

/** Filled pie styled as a poker chip: slices inside, a dark rim with light edge spots, labels outside. */
export function ChipPie({
  items,
  unit = 'combos',
  className,
  labels = true,
  svgRef,
}: {
  items: Item[];
  unit?: Unit;
  className?: string;
  labels?: boolean;
  svgRef?: Ref<SVGSVGElement>;
}) {
  const ss = slices(items, unit);
  return (
    <svg
      ref={svgRef}
      viewBox="0 0 200 200"
      className={className}
      role="img"
      aria-label={`${total(items)}`}
    >
      <circle cx={C} cy={C} r={R} fill="var(--surface-2)" />
      {ss.map((s) => {
        if (s.share >= 1) return <circle key={s.i} cx={C} cy={C} r={R} fill={s.color} />;
        const [sx, sy] = pt(s.start, R);
        const [ex, ey] = pt(s.end, R);
        return (
          <path
            key={s.i}
            d={`M${C} ${C} L${sx} ${sy} A${R} ${R} 0 ${s.share > 0.5 ? 1 : 0} 1 ${ex} ${ey} Z`}
            fill={s.color}
            stroke="var(--fg)"
            strokeWidth="0.4"
          />
        );
      })}
      <circle cx={C} cy={C} r={R} fill="none" stroke="var(--fg)" strokeWidth="0.6" opacity="0.9" />
      {labels &&
        placeLabels(ss).map(({ s, x, y, anchor, lx, ly }) => (
          <g key={s.i} fill={s.color}>
            <polyline
              points={`${lx},${ly} ${x + (anchor === 'start' ? -3 : 3)},${y}`}
              fill="none"
              stroke={s.color}
              strokeWidth="0.5"
              opacity="0.8"
            />
            <text
              x={x}
              textAnchor={anchor}
              dominantBaseline="central"
              fontFamily="var(--font-mono)"
              fontWeight="700"
              fontSize="8"
            >
              {s.lines.map((line, k) => (
                <tspan key={k} x={x} y={y - 4 - 6.5 * (s.lines.length - 1 - k)} fontSize="6">
                  {line}
                </tspan>
              ))}
              <tspan x={x} y={s.lines.length ? y + 3.5 : y}>
                {Math.round(s.share * 100)}%
              </tspan>
            </text>
          </g>
        ))}
    </svg>
  );
}

/** the client's right-hand ruler: each percent sits at its own height on the stack, the number in brackets and the bet size highlighted */
const STACK_TICKS: { l: string; r: number }[] = [
  { l: '1/4', r: 80 },
  { l: '1/3', r: 75 },
  { l: '1/2', r: 67 },
  { l: '2/3', r: 60 },
  { l: '3/4', r: 57 },
  { l: '1x', r: 50 },
  { l: '1+1/4', r: 44 },
  { l: '1+1/2', r: 40 },
  { l: '2x', r: 33 },
  { l: '2x+1/2', r: 29 },
  { l: '3x', r: 25 },
];

/** Spread sorted positions so neighbours sit at least `gap` apart, inside [lo, hi]. */
function spread(ys: number[], gap: number, lo: number, hi: number): number[] {
  const out = [...ys];
  for (let k = 0; k < out.length; k++) out[k] = Math.max(out[k]!, k ? out[k - 1]! + gap : lo);
  for (let k = out.length - 1; k >= 0; k--)
    out[k] = Math.min(out[k]!, k + 1 < out.length ? out[k + 1]! - gap : hi);
  for (let k = 0; k < out.length; k++) out[k] = Math.max(out[k]!, k ? out[k - 1]! + gap : lo);
  return out;
}

/**
 * One stack of 20 poker chips (one chip = 5 %), seen slightly from above, as tall as the pie: every share is a
 * run of chips in its pie colour with curved edges. The left ruler is 0–100 % in 5 % steps (one per chip); the
 * right one carries the client's percents at their heights, each with its bet size in brackets.
 */
function ChipStack({ items, unit, height }: { items: Item[]; unit: Unit; height: number }) {
  const { t } = useTranslation();
  const ss = slices(items, unit);
  // same vertical span as the chip in the pie's 200-unit box (centre 100, outer radius R + 6)
  const top = C - (R + 6);
  const h = 2 * (R + 6);
  const bw = 30;
  const bx = 40 - bw / 2;
  const rx = bw / 2;
  const ry = 3;
  const chip = h / 20;
  // runs stack from the top down: the first input (the strongest hand) is the top run
  const runs = ss.reduce<{ s: Slice; y: number; sh: number }[]>((acc, s) => {
    const sh = s.share * h;
    const prev = acc[acc.length - 1];
    return [...acc, { s, y: prev ? prev.y + prev.sh : top, sh }];
  }, []);
  const topColor = runs.length ? runs[0]!.s.color : 'var(--surface-2)';
  /** a band of the cylinder from y0 down to y1: both edges bulge downwards, as seen from above */
  const band = (y0: number, y1: number): string =>
    `M${bx} ${y0} a${rx} ${ry} 0 0 0 ${bw} 0 L${bx + bw} ${y1} a${rx} ${ry} 0 0 1 ${-bw} 0 Z`;
  const seam = (y: number): string => `M${bx} ${y} a${rx} ${ry} 0 0 0 ${bw} 0`;
  /** the curve of a chip edge at height y: an elliptical arc from x1 to x2, bulging downwards */
  const along = (y: number, x1: number, x2: number): string => {
    const yAt = (x: number): number =>
      y + ry * Math.sqrt(Math.max(0, 1 - ((x - bx - rx) / rx) ** 2));
    return `M${x1} ${yAt(x1)} A${rx} ${ry} 0 0 0 ${x2} ${yAt(x2)}`;
  };
  const yOf = (p: number): number => top + h - (p / 100) * h;
  /** right-hand labels: sorted top-down and nudged apart where two percents are closer than a label */
  /** the right labels hang from the top: a percent of r sits r % of the way DOWN the stack */
  const yRight = (p: number): number => top + (p / 100) * h;
  const right = [...STACK_TICKS].sort((a, b) => a.r - b.r);
  const rightY = spread(
    right.map((tk) => yRight(tk.r)),
    4.8,
    top,
    top + h,
  );
  const font = { fontFamily: 'var(--font-mono)', fontSize: 4.2, fill: 'var(--fg-muted)' };
  const leftX = bx - 5;
  const rightX = bx + bw + 5;
  return (
    <svg
      viewBox="0 0 96 200"
      className="max-w-full"
      style={height ? { height } : undefined}
      role="img"
      aria-label={`${total(items)}`}
    >
      {/* left ruler: 0–100 %, a label per chip */}
      <g {...font}>
        <line
          x1={leftX}
          y1={top}
          x2={leftX}
          y2={top + h}
          stroke="var(--fg-muted)"
          strokeWidth="0.6"
        />
        {Array.from({ length: 21 }, (_, k) => (
          <g key={k}>
            <line
              x1={leftX - 2}
              y1={yOf(k * 5)}
              x2={leftX}
              y2={yOf(k * 5)}
              stroke="var(--fg-muted)"
              strokeWidth="0.5"
            />
            <text x={leftX - 3.5} y={yOf(k * 5)} textAnchor="end" dominantBaseline="central">
              {k * 5}
            </text>
          </g>
        ))}
        <text x={leftX} y={top - 6} textAnchor="middle">
          (%)
        </text>
        <text x={leftX} y={top + h + 8} textAnchor="middle">
          (%)
        </text>
      </g>
      {/* right ruler: the client's percents at their heights, bet size in brackets */}
      <g {...font}>
        <line
          x1={rightX}
          y1={top}
          x2={rightX}
          y2={top + h}
          stroke="var(--fg-muted)"
          strokeWidth="0.6"
        />
        {Array.from({ length: 21 }, (_, k) => (
          <line
            key={k}
            x1={rightX}
            y1={yOf(k * 5)}
            x2={rightX + 1.5}
            y2={yOf(k * 5)}
            stroke="var(--fg-muted)"
            strokeWidth="0.5"
          />
        ))}
        {right.map((tk, k) => (
          <g key={tk.r}>
            <polyline
              points={`${rightX},${yRight(tk.r)} ${rightX + 2.5},${yRight(tk.r)} ${rightX + 4},${rightY[k]!}`}
              fill="none"
              stroke="var(--fg-muted)"
              strokeWidth="0.5"
            />
            <text x={rightX + 5.5} y={rightY[k]!} textAnchor="start" dominantBaseline="central">
              <tspan fill="var(--fg)" fontWeight="700">
                {tk.l}
              </tspan>{' '}
              ({tk.r}%)
            </text>
          </g>
        ))}
      </g>
      {/* the client's caption under the stack, centred on the drawing */}
      <text
        x={48}
        y={top + h + 22}
        textAnchor="middle"
        fontFamily="var(--font-mono)"
        fontSize="4.2"
        fill="var(--fg-muted)"
      >
        {t('morphology.strongest')}
      </text>
      {/* the stack, bottom-up: empty stack, colour runs, a curved seam per chip, then the edge spots */}
      <path d={band(top, top + h)} fill="var(--surface-2)" />
      {runs.map(({ s, y, sh }) => (
        <path key={s.i} d={band(y, y + sh)} fill={s.color} />
      ))}
      {Array.from({ length: 21 }, (_, k) => (
        <path
          key={k}
          d={seam(top + h - k * chip)}
          fill="none"
          stroke="rgba(0,0,0,0.45)"
          strokeWidth="0.7"
        />
      ))}
      {/* three light edge spots per chip, each following the chip's curve */}
      {Array.from({ length: 20 }, (_, k) =>
        [3, 13, 23].map((sx) => (
          <path
            key={`${k}-${sx}`}
            d={along(top + h - (k + 0.5) * chip, bx + sx, bx + sx + 4)}
            fill="none"
            stroke="rgba(255,255,255,0.85)"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        )),
      )}
      {/* the top chip's face, seen from above */}
      <ellipse
        cx={bx + rx}
        cy={top}
        rx={rx}
        ry={ry}
        fill={topColor}
        stroke="rgba(0,0,0,0.45)"
        strokeWidth="0.7"
      />
      <ellipse cx={bx + rx} cy={top} rx={rx} ry={ry} fill="rgba(255,255,255,0.22)" />
      <ellipse
        cx={bx + rx}
        cy={top}
        rx={rx - 5}
        ry={ry - 1}
        fill="none"
        stroke="rgba(255,255,255,0.6)"
        strokeWidth="0.5"
        strokeDasharray="2 2"
      />
    </svg>
  );
}
